import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";

const inputCls = "w-16 rounded-md border border-line bg-bg px-2 py-1 text-xs text-ink-primary text-center focus:outline-none focus:border-brand-amber/60";
const PRIORITIES = ["urgent", "high", "normal", "low"];

const ComplianceRow = ({ p, fr, rs }) => (
  <div className="grid grid-cols-4 items-center gap-2 py-1.5 border-b border-line last:border-0 text-xs" data-testid={`sla-row-${p}`}>
    <span className="font-mono text-[10px] uppercase tracking-wider text-ink-secondary">{p}</span>
    <span className="text-ink-primary">{fr.responded}/{fr.total} responded</span>
    <span className={fr.compliance_pct == null ? "text-ink-muted" : fr.compliance_pct >= 90 ? "text-emerald-300" : fr.compliance_pct >= 70 ? "text-amber-300" : "text-rose-300"}>
      {fr.compliance_pct == null ? "—" : `${fr.compliance_pct}% in SLA`}
    </span>
    <span className={rs.compliance_pct == null ? "text-ink-muted" : rs.compliance_pct >= 90 ? "text-emerald-300" : "text-amber-300"}>
      {rs.compliance_pct == null ? "— resolution" : `${rs.compliance_pct}% resolution`}
    </span>
  </div>
);

export default function AdminSlaTab() {
  const [data, setData] = useState(null);
  const [sla, setSla] = useState(null);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/admin/helpdesk/analytics?days=30").then((r) => { setData(r.data); setSla(r.data.sla); }).catch(() => {});
    api.get("/admin/impersonation-log").then((r) => setLog(r.data.log)).catch(() => {});
  };
  useEffect(load, []);

  const saveSla = async () => {
    setBusy(true);
    try { await api.put("/admin/helpdesk/sla", sla); toast.success("SLA targets saved"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const setTarget = (group, p, v) =>
    setSla({ ...sla, [group]: { ...sla[group], [p]: Number(v) || 0 } });

  if (!data) return <div className="text-sm text-ink-muted py-6">Loading analytics…</div>;
  const maxTrend = Math.max(1, ...data.trend.map((d) => d.count));

  return (
    <div className="space-y-6" data-testid="admin-sla-tab">
      {/* Compliance */}
      <div className="rounded-xl border border-line bg-bg-surface p-4">
        <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-3">
          SLA compliance — last {data.days} days ({data.total_tickets} tickets)
        </div>
        {PRIORITIES.map((p) => (
          <ComplianceRow key={p} p={p} fr={data.first_response[p]} rs={data.resolution[p]} />
        ))}
      </div>

      {/* Breaches */}
      <div className="rounded-xl border border-line bg-bg-surface p-4" data-testid="sla-breaches">
        <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-3 flex items-center gap-1.5">
          <ShieldAlert className="h-3.5 w-3.5 text-rose-300" /> Open tickets past first-response target ({data.breaches.length})
        </div>
        {data.breaches.length === 0
          ? <div className="text-xs text-emerald-300">No SLA breaches — every open ticket is within target.</div>
          : data.breaches.map((b) => (
            <div key={b.reference} className="flex flex-wrap items-center gap-2 py-1.5 border-b border-line last:border-0 text-xs">
              <span className="font-mono text-[10px] text-brand-amber">{b.reference}</span>
              <span className="text-ink-secondary truncate flex-1">{b.subject}</span>
              <span className="font-mono text-[9px] uppercase text-ink-muted">{b.priority}</span>
              <span className="text-rose-300 font-semibold">{b.age_hours}h old (target {b.target_hours}h)</span>
            </div>
          ))}
      </div>

      {/* Volume trend */}
      <div className="rounded-xl border border-line bg-bg-surface p-4">
        <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-3">Ticket volume — daily</div>
        <div className="flex items-end gap-[3px] h-24" data-testid="sla-trend-chart">
          {data.trend.map((d) => (
            <div key={d.date} title={`${d.date}: ${d.count}`} className="flex-1 rounded-t bg-brand-amber/60 hover:bg-brand-amber transition"
              style={{ height: `${Math.max(3, (d.count / maxTrend) * 100)}%` }} />
          ))}
        </div>
        <div className="flex justify-between font-mono text-[8px] text-ink-muted mt-1">
          <span>{data.trend[0]?.date}</span><span>{data.trend[data.trend.length - 1]?.date}</span>
        </div>
      </div>

      {/* Targets editor */}
      {sla && (
        <div className="rounded-xl border border-line bg-bg-surface p-4 max-w-xl" data-testid="sla-targets-editor">
          <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-3">SLA targets (hours)</div>
          <div className="grid grid-cols-3 gap-2 items-center text-xs mb-1.5">
            <span /><span className="text-ink-muted">First response</span><span className="text-ink-muted">Resolution</span>
          </div>
          {PRIORITIES.map((p) => (
            <div key={p} className="grid grid-cols-3 gap-2 items-center py-1">
              <span className="font-mono text-[10px] uppercase tracking-wider text-ink-secondary">{p}</span>
              <input type="number" min="1" className={inputCls} value={sla.first_response_hours[p]}
                data-testid={`sla-fr-${p}`} onChange={(e) => setTarget("first_response_hours", p, e.target.value)} />
              <input type="number" min="1" className={inputCls} value={sla.resolution_hours[p]}
                data-testid={`sla-rs-${p}`} onChange={(e) => setTarget("resolution_hours", p, e.target.value)} />
            </div>
          ))}
          <button type="button" onClick={saveSla} disabled={busy} data-testid="sla-save-btn"
            className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-brand-amber px-4 py-2 text-xs font-semibold text-black disabled:opacity-50">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save targets
          </button>
        </div>
      )}

      {/* Avg resolution by category */}
      <div className="rounded-xl border border-line bg-bg-surface p-4 max-w-xl">
        <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-3">Avg resolution by category (h)</div>
        {Object.keys(data.avg_resolution_by_category).length === 0
          ? <div className="text-xs text-ink-muted">No resolved tickets in window.</div>
          : Object.entries(data.avg_resolution_by_category).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
            <div key={k} className="flex justify-between py-1 text-xs border-b border-line last:border-0">
              <span className="text-ink-secondary">{k.replace(/_/g, " ")}</span>
              <span className="text-ink-primary font-semibold">{v}h</span>
            </div>
          ))}
      </div>

      {/* Impersonation audit log */}
      <div className="rounded-xl border border-line bg-bg-surface p-4" data-testid="impersonation-log">
        <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-3">Impersonation audit log (last 20)</div>
        {log.length === 0
          ? <div className="text-xs text-ink-muted">No impersonation sessions recorded.</div>
          : log.map((l) => (
            <div key={l.log_id} className="flex flex-wrap items-center gap-2 py-1.5 border-b border-line last:border-0 text-xs">
              <span className="text-ink-primary">{l.admin_email}</span>
              <span className="text-ink-muted">viewed as</span>
              <span className="text-ink-primary">{l.target_email}</span>
              {l.reason && <span className="text-ink-muted truncate">· {l.reason}</span>}
              <span className={`ml-auto font-mono text-[9px] uppercase ${l.status === "active" ? "text-amber-300" : "text-ink-muted"}`}>
                {l.status} · {new Date(l.started_at).toLocaleString()}
              </span>
            </div>
          ))}
      </div>
    </div>
  );
}
